import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import PaymentMethodIcons from './PaymentMethodIcons';

const StarterPackCard = ({ variant = 'landing', onPurchaseComplete }) => {
  const [purchaseStatus, setPurchaseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    checkPurchaseStatus();
  }, []);

  const checkPurchaseStatus = async () => {
    try {
      const response = await api.get(`/starterpack/status`, {
        withCredentials: true
      });
      setPurchaseStatus(response.data);
    } catch (error) {
      // User not logged in or error - that's ok
      console.log('Not logged in or error checking starter pack status');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      setPurchasing(true);
      
      // Check if user is authenticated first
      try {
        await api.get('/auth/me', { withCredentials: true });
      } catch (authError) {
        // Not authenticated - redirect to login
        localStorage.setItem('wtpl_intent', 'starter_pack');
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        return;
      }
      
      const response = await api.post(
        `/starterpack/create-checkout`,
        {},
        { 
          withCredentials: true,
          timeout: 30000 
        }
      );
      
      // Redirect to Stripe checkout (mobile-friendly)
      if (response.data.url) {
        setTimeout(() => {
          window.location.assign(response.data.url);
        }, 100);
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      setPurchasing(false);
      if (error.response?.status === 401) {
        // Redirect to login
        localStorage.setItem('wtpl_intent', 'starter_pack');
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      } else if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Error al procesar la compra. Intenta de nuevo.');
      }
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      
      const response = await api.post(
        `/starterpack/download`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
      
      if (onPurchaseComplete) onPurchaseComplete();
    } catch (error) {
      console.error('Error downloading:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Error al descargar. Intenta de nuevo.');
      }
    } finally {
      setDownloading(false);
    }
  };

  const hasPurchased = purchaseStatus?.purchased;

  // Landing variant - full card (SILVER Diamond Style)
  if (variant === 'landing') {
    return (
      <div className="bg-[#141414] border-2 border-[#C0C0C0] rounded-2xl p-6 relative shadow-lg shadow-[#C0C0C0]/10 flex flex-col h-full">
        {/* Diamond Badge */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-[#E8E8E8] to-[#A0A0A0] text-black text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">PRINCIPIANTES</span>
        </div>
        
        {/* Content */}
        <h3 className="text-xl font-bold mb-2 mt-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>WTPL Starter Pack</h3>
        <div className="flex items-baseline gap-1 mb-2">
          <span className="text-4xl font-black bg-gradient-to-b from-white via-[#E0E0E0] to-[#A0A0A0] bg-clip-text text-transparent">$39</span>
          <span className="text-[#888]">USD</span>
        </div>
        <p className="text-sm text-[#666] mb-5">Pago único</p>
        
        {/* Features */}
        <ul className="space-y-3 mb-6 flex-1">
          <li className="flex items-start gap-3 text-sm">
            <span className="text-[#C0C0C0]">✔</span>
            <span className="text-[#B3B3B3]">800-1,000 tracks seleccionados</span>
          </li>
          <li className="flex items-start gap-3 text-sm">
            <span className="text-[#C0C0C0]">✔</span>
            <span className="text-[#B3B3B3]">Reggaeton + Open Format</span>
          </li>
          <li className="flex items-start gap-3 text-sm">
            <span className="text-[#C0C0C0]">✔</span>
            <span className="text-[#B3B3B3]">Crates listos para eventos</span>
          </li>
          <li className="flex items-start gap-3 text-sm">
            <span className="text-[#C0C0C0]">✔</span>
            <span className="text-[#B3B3B3]">Ideal para DJs principiantes</span>
          </li>
          <li className="flex items-start gap-3 text-sm">
            <span className="text-[#C0C0C0]">✔</span>
            <span className="text-[#B3B3B3]">Descarga inmediata</span>
          </li>
        </ul>
        
        {/* Button */}
        <div className="mt-auto space-y-2">
          {hasPurchased ? (
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full bg-gradient-to-r from-[#00D9FF] to-[#00D9FF] hover:opacity-90 font-bold py-4 rounded-xl text-black"
            >
              {downloading ? 'Preparando...' : 'Descargar'}
            </Button>
          ) : (
            <Button
              onClick={handlePurchase}
              disabled={purchasing || loading}
              className="w-full bg-gradient-to-r from-[#E8E8E8] via-white to-[#C0C0C0] hover:opacity-90 font-bold py-4 rounded-xl text-black border border-white/30"
              data-testid="buy-starterpack-btn"
            >
              {purchasing ? '...' : 'Obtener Starter Pack'}
            </Button>
          )}
          {hasPurchased ? <div className="py-3"></div> : <PaymentMethodIcons className="pt-2 pb-1" showLabel={false} />}
        </div>
      </div>
    );
  }

  // Dashboard variant - compact
  return (
    <div className="relative bg-gradient-to-r from-[#1a1a1a] to-[#141414] border border-[#333] rounded-xl p-4 overflow-hidden hover:border-[#666] transition-all">
      <div className="absolute inset-0 bg-gradient-to-r from-white/[0.03] via-transparent to-white/[0.02] pointer-events-none"></div>
      
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8E8E8] via-[#C0C0C0] to-[#888] flex items-center justify-center shadow-lg shadow-white/10 border border-white/20 flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M6 3h12l4 6-10 13L2 9z"/></svg>
          </div>
          <div>
            <h4 className="font-bold text-white flex items-center gap-2">
              WTPL Starter Pack
              <span className="text-[10px] bg-gradient-to-r from-[#E8E8E8] to-[#888] text-black px-2 py-0.5 rounded font-bold">PRINCIPIANTE</span>
            </h4>
            <p className="text-sm text-[#888]">
              800-1,000 tracks • Reggaeton + Open Format
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-2xl font-bold bg-gradient-to-b from-white to-[#A0A0A0] bg-clip-text text-transparent">$39</p>
            <p className="text-xs text-[#666]">USD</p>
          </div>
          
          {hasPurchased ? (
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-gradient-to-r from-[#00D9FF] to-[#00D9FF] hover:opacity-90 font-bold rounded-lg px-4 text-black"
            >
              {downloading ? '...' : 'Descargar'}
            </Button>
          ) : (
            <Button
              onClick={handlePurchase}
              disabled={purchasing || loading}
              className="bg-gradient-to-r from-[#E8E8E8] via-white to-[#C0C0C0] hover:opacity-90 font-bold rounded-lg px-4 text-black border border-white/30"
            >
              {purchasing ? '...' : 'Comprar'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StarterPackCard;
